import { produce } from 'immer';
import _ from 'lodash';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';
import { customEvents } from '../../../../common/constants';
import { showErrorToast } from '../../../../common/global/helpers';
import Button from '../../../../common/widgets/Button';
import Checkbox from '../../../../common/widgets/Checkbox';
import Dialog from '../../../../common/widgets/Dialog';
import Icons from '../../../../common/widgets/Icons';
import { CustomerInfoContext } from '../../contexts/CustomerInfoContext';
import { OptInSourceTypes, OptInTypes } from '../../enums';
import { ErrorState } from '../../models/Errors';
import { resources } from '../../resources/resources';
import { optInService } from '../../services/opt-in';

export const AcceptTcAndPpDialog = (props: { closeDialog: () => void }) => {
  const { closeDialog } = props;
  const { t: translate } = useTranslation();
  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);
  const auth = useAuth();
  const [acceptedTc, setAcceptedTc] = useState<boolean>(false);
  const [acceptedPp, setAcceptedPp] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});

  const onContinueClick = async () => {
    const newErrors: ErrorState = {};
    if (!acceptedTc) {
      newErrors.acceptedTc = [
        {
          target: 'acceptedTc',
          code: resources.isRequired,
          severity: 'error',
          placeholders: {},
        },
      ];
    }
    if (!acceptedPp) {
      newErrors.acceptedPp = [
        {
          target: 'acceptedPp',
          code: resources.isRequired,
          severity: 'error',
          placeholders: {},
        },
      ];
    }
    setErrors(newErrors);
    if (!_.isEmpty(newErrors)) return;

    setIsLoading(true);
    const resp = await optInService.consent(
      OptInTypes.TCPP,
      true,
      OptInSourceTypes.RegisterAccount,
    );
    setIsLoading(false);
    debugger;
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.generic));
    } else {
      setCustomerInfo(
        produce(customerInfo, d => {
          d.acceptedTcAndPp = true;
        }),
      );

      document.dispatchEvent(
        new CustomEvent(customEvents.refreshCustomerBalances),
      );
      closeDialog();
    }
  };

  const clearErrors = (field: keyof typeof errors) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  const handleLogout = async () => {
    try {
      await auth.signoutRedirect();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <Dialog
      showCloseIcon={false}
      closeOnClickOutside={false}
      className="dialog-m"
    >
      <div className="dialog-content">
        <div className="dialog-image">
          <Icons.WarningRed />
        </div>
        <div className="dialog-title">
          {translate(resources.acceptTcAndPpDialog.title)}
        </div>
        <div className="dialog-text">
          <Checkbox
            label={
              (
                <label className="k-checkbox-label">
                  {translate(resources.acceptTcAndPpDialog.termsLabel)}{' '}
                  <Link to={translate(resources.links.termsAndConditions.link)}>
                    {translate(
                      resources.acceptTcAndPpDialog.termsAndConditions,
                    )}
                  </Link>
                  *
                </label>
              ) as any
            }
            isChecked={acceptedTc}
            onChange={e => setAcceptedTc(e.value)}
            errors={errors.acceptedTc}
            clearErrors={() => clearErrors('acceptedTc')}
          />
          <Checkbox
            label={
              (
                <label className="k-checkbox-label">
                  {translate(resources.acceptTcAndPpDialog.privacyLabel)}{' '}
                  <Link to={translate(resources.links.personalData.link)}>
                    {translate(resources.acceptTcAndPpDialog.privacyPolicy)}
                  </Link>
                  *
                </label>
              ) as any
            }
            isChecked={acceptedPp}
            onChange={e => setAcceptedPp(e.value)}
            errors={errors.acceptedPp}
            clearErrors={() => clearErrors('acceptedPp')}
          />
        </div>
      </div>
      <div className="dialog-actions vertical">
        <Button
          onClick={onContinueClick}
          loading={isLoading}
          className="btn btn-solid"
        >
          {translate(resources.continue)}
        </Button>
        <Button
          onClick={handleLogout}
          className="btn btn-outline"
          disabled={isLoading}
        >
          {translate(resources.doNotAgree)}
        </Button>
      </div>
    </Dialog>
  );
};
